import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api, setAccessToken } from "../api/client";
import type { CurrentUser, TokenPair } from "../api/types";

interface AuthContextValue {
  user: CurrentUser | null;
  /** 首次静默刷新中（用于避免登录页闪烁） */
  initializing: boolean;
  loginWithDev: (wechatUserId: string) => Promise<void>;
  startWechatLogin: () => Promise<void>;
  completeWechatLogin: (params: { code: string; state: string }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** access token 有效期 15 分钟，提前 2 分钟静默续期 */
const REFRESH_INTERVAL_MS = 13 * 60 * 1000;

function applyTokens(pair: TokenPair): void {
  setAccessToken(pair.accessToken);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [initializing, setInitializing] = useState(true);
  const refreshTimer = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (refreshTimer.current !== null) {
      window.clearInterval(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const startAutoRefresh = useCallback(() => {
    clearTimer();
    refreshTimer.current = window.setInterval(() => {
      void api.auth
        .refresh()
        .then(applyTokens)
        .catch(() => {
          // 刷新失败（cookie 过期/被吊销）：退回登录页
          setAccessToken(null);
          setUser(null);
        });
    }, REFRESH_INTERVAL_MS);
  }, [clearTimer]);

  // 首次进入：尝试用 refresh cookie 静默恢复登录态
  useEffect(() => {
    let cancelled = false;
    void api.auth
      .refresh()
      .then((pair) => {
        if (cancelled) return;
        applyTokens(pair);
        setUser(pair.user);
        startAutoRefresh();
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setInitializing(false);
      });
    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [startAutoRefresh, clearTimer]);

  const loginWithDev = useCallback(
    async (wechatUserId: string) => {
      const pair = await api.auth.devLogin(wechatUserId);
      applyTokens(pair);
      setUser(pair.user);
      startAutoRefresh();
    },
    [startAutoRefresh],
  );

  const startWechatLogin = useCallback(async () => {
    // 企业微信客户端内 → 网页授权静默登录；普通浏览器 → Web 扫码登录（企微 App 扫码确认）
    const inWecom = /wxwork/i.test(navigator.userAgent);
    const { url } = await api.auth.wechatUrl(inWecom ? undefined : "qr");
    // mock 模式下 url 指向前端回调页并附带演示 code，体验与真实扫码一致
    window.location.href = url;
  }, []);

  const completeWechatLogin = useCallback(
    async (params: { code: string; state: string }) => {
      const pair = await api.auth.wechatCallback(params);
      applyTokens(pair);
      setUser(pair.user);
      startAutoRefresh();
    },
    [startAutoRefresh],
  );

  const logout = useCallback(async () => {
    clearTimer();
    try {
      await api.auth.logout();
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  }, [clearTimer]);

  return (
    <AuthContext.Provider
      value={{ user, initializing, loginWithDev, startWechatLogin, completeWechatLogin, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return ctx;
}
